import React, { useEffect, useState } from "react";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import format from "date-fns/format";
import parse from "date-fns/parse";
import startOfWeek from "date-fns/startOfWeek";
import getDay from "date-fns/getDay";
import enUS from "date-fns/locale/en-US";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ExternalLink, MapPin, Plane, Video } from "lucide-react";
import "react-big-calendar/lib/css/react-big-calendar.css";

const locales = { "en-US": enUS };
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

/* ---------------------------
   Small helper: compare dates (date-only)
   --------------------------- */
const sameDay = (a, b) => {
  const da = new Date(a);
  const db = new Date(b);
  return (
    da.getFullYear() === db.getFullYear() &&
    da.getMonth() === db.getMonth() &&
    da.getDate() === db.getDate()
  );
};

const dateInRange = (date, start, end) => {
  const d = new Date(date).setHours(0, 0, 0, 0);
  const s = new Date(start).setHours(0, 0, 0, 0);
  const e = new Date(end || start).setHours(0, 0, 0, 0);
  return d >= s && d <= e;
};

/* ---------------------------
   Tooltips
   --------------------------- */
const TaskTooltip = ({ task }) => {
  const hasValidDate = task?.date && !isNaN(new Date(task.date).getTime());
  const isInProgress = !hasValidDate;

  return (
    <div className="p-2 max-w-xs text-white">
      <div className="font-semibold text-sm mb-1">{task.title}</div>
      {hasValidDate && (
        <div className="text-sm text-white mb-1">
          Due: {format(new Date(task.date), "PPP")}
        </div>
      )}
      <div className="text-sm">
        Status:{" "}
        <span
          className={`font-medium ${
            isInProgress
              ? "text-yellow-400"
              : task.status === "Backlog"
                ? "text-red-400"
                : "text-green-400"
          }`}
        >
          {isInProgress ? "In progress" : task.status}
        </span>
      </div>
      {task.project_name && (
        <div className="text-sm text-white mt-1">
          Project: {task.project_name}
        </div>
      )}
    </div>
  );
};

const TasksTooltip = ({ event, tasks }) => {
  const hoursWorked = event.resource?.hoursWorked;
  const varianceFlag = event.resource?.varianceFlag;
  const isNegativeVariance = varianceFlag === "NEGATIVE";

  return (
    <div className="p-2 max-w-xs">
      <div className="mb-3 pb-2 border-b">
        <div className="font-semibold text-sm text-gray-900">{event.title}</div>
        <div
          className={`text-sm font-medium ${isNegativeVariance ? "text-red-600" : "text-green-600"}`}
        >
          {hoursWorked !== undefined
            ? `${hoursWorked.toFixed(1)} hours worked`
            : "Present"}
        </div>
        {varianceFlag && (
          <div
            className={`text-xs ${isNegativeVariance ? "text-red-500" : "text-green-500"}`}
          >
            {isNegativeVariance ? "Negative variance" : "Positive variance"}
          </div>
        )}
      </div>

      {tasks && tasks.length > 0 ? (
        <>
          <div className="font-semibold text-sm mb-2 text-gray-900">Tasks:</div>
          <div className="space-y-2">
            {tasks.map((task, index) => (
              <div key={index} className="text-sm">
                <div className="font-medium text-gray-800">
                  • {task.task_title}
                </div>
                {task.project_name && (
                  <div className="text-gray-600 text-xs ml-2">
                    Project: {task.project_name}
                  </div>
                )}
                {task.description && (
                  <div className="text-gray-500 text-xs ml-2 mt-1 line-clamp-2">
                    {task.description}
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      ) : (
        <div className="text-sm text-gray-500">No tasks assigned</div>
      )}
    </div>
  );
};

/* ---------------------------
   Small event badge component for tasks
   --------------------------- */
const TaskEvent = ({ event, onBadgeClick }) => {
  const isTaskbar = event.resource?.eventType === "taskbar";
  const isBacklog = event.resource?.status === "Backlog";
  const isDone = event.resource?.status === "Done";

  const base = "text-xs px-2 py-0.5 rounded cursor-pointer inline-block";
  const colorClass = isTaskbar
    ? "bg-purple-600 text-white"
    : isBacklog
      ? "bg-red-600 text-white"
      : isDone
        ? "bg-green-600 text-white"
        : "bg-blue-600 text-white";

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div>
            <button
              type="button"
              className={`${base} ${colorClass}`}
              onClick={(e) => {
                e.stopPropagation();
                onBadgeClick(event);
              }}
            >
              {event.title}
            </button>
          </div>
        </TooltipTrigger>
        <TooltipContent side="top" className="w-72">
          <TaskTooltip task={event.resource} />
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

/* ---------------------------
   CustomEvent renderer (keeps all behavior)
   --------------------------- */
const CustomEvent = ({ event, onBadgeClick }) => {
  // Holiday: we keep title visible, but ribbon is handled in day cell styling.
  if (event.resource?.isHoliday) {
    return (
      <div
        onClick={(e) => {
          e.stopPropagation();
          onBadgeClick(event);
        }}
        className="text-xs w-full truncate font-medium"
        style={{ color: "#065f46" }} // dark green text for contrast on white cell
      >
        {event.title}
      </div>
    );
  }

  if (event.resource?.isEvent) {
    return (
      <div
        onClick={(e) => {
          e.stopPropagation();
          onBadgeClick(event);
        }}
        className="bg-blue-500 text-white rounded px-2 py-1 text-xs w-full text-center"
      >
        {event.title}
      </div>
    );
  }

  if (event.resource?.isGoogleEvent) {
    return (
      <div
        onClick={(e) => {
          e.stopPropagation();
          onBadgeClick(event);
        }}
        className="flex items-center gap-1 w-full rounded px-2 py-0.5 text-xs font-medium cursor-pointer border-l-4 bg-red-50 text-red-800"
        style={{ borderLeftColor: "#ea4335" }}
        title={event.title}
      >
        {event.resource?.meetLink && (
          <Video className="h-3 w-3 shrink-0 text-red-600" />
        )}
        <span className="truncate">{event.title}</span>
      </div>
    );
  }

  // Approved leave for the day. Without this branch it fell through to the
  // untyped default at the bottom and rendered as bare unstyled text.
  if (event.resource?.isLeaveSummary) {
    const names = (event.resource.users_on_leave || []).map((u) => u.name);
    return (
      <div
        onClick={(e) => {
          e.stopPropagation();
          onBadgeClick(event);
        }}
        className="flex items-center gap-1 w-full rounded px-2 py-0.5 text-xs font-medium cursor-pointer border-l-4 bg-amber-50 text-amber-900"
        style={{ borderLeftColor: "#f59e0b" }}
        title={names.length ? `On leave: ${names.join(", ")}` : event.title}
      >
        <Plane className="h-3 w-3 shrink-0 text-amber-600" />
        <span className="truncate">
          {names.length === 1 ? names[0] : `${event.resource.count} on leave`}
        </span>
      </div>
    );
  }

  /*
  ===============================
  TO SHOW TOTAL WORKFORCE HOURS
  ===============================
  */

if (event.resource?.isAggregatedAttendance) {
  const eventContent = (
    <div className="border rounded p-1 text-xs bg-white">
      <div className="flex justify-between items-start">
        <div
          className="flex-1 border-l-4 p-1 rounded bg-white cursor-pointer text-center"
          style={{
            borderLeftColor: "#bbf7d0", // light green
          }}
          onClick={(e) => {
            e.stopPropagation();
            onBadgeClick(event);
          }}
        >
          <div className="font-semibold text-gray-900 text-[10px] leading-tight uppercase tracking-wide">
          Total Workforce Hours
          </div>

          <div className="text-gray-800 font-bold text-sm leading-tight">
            {Math.round(event.resource.totalHours.toFixed(1))}h
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div>{eventContent}</div>
        </TooltipTrigger>

        <TooltipContent side="top" align="start" className="w-80">
          {/* OPTIONAL: show workforce details */}
          <div className="text-sm text-gray-700">
            Total Workforce Hours: {Math.round(event.resource.totalHours.toFixed(1))}h
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}





  if (event.resource?.isPresent) {
    const hoursWorked = event.resource.hoursWorked;
    const varianceFlag = event.resource.varianceFlag;
    const isNegativeVariance = varianceFlag === "NEGATIVE";
    const hasTasks =
      event.resource.currentTasks && event.resource.currentTasks.length > 0;

    const eventContent = (
      <div className="border rounded p-1 text-xs bg-white">
        <div className="flex justify-between items-start">
          <div
            className="flex-1 border-l-4 p-1 rounded bg-white cursor-pointer"
            style={{
              borderLeftColor: isNegativeVariance ? "#fecaca" : "#bbf7d0",
            }}
            onClick={(e) => {
              e.stopPropagation();
              onBadgeClick(event);
            }}
          >
            <div className="font-semibold text-gray-900">{event.title}</div>
            <div className="text-gray-700">
              {hoursWorked !== undefined
                ? `${hoursWorked.toFixed(1)}h`
                : "Present"}
            </div>
          </div>

          {hasTasks && (
            <div className="flex-1 ml-2 border-l pl-2">
              <div className="text-xs font-medium text-gray-700">Tasks:</div>
              {event.resource.currentTasks.slice(0, 2).map((task, idx) => (
                <div key={idx} className="text-xs text-gray-600 truncate">
                  • {task.task_title}
                  {task.project_name ? ` (${task.project_name})` : ""}
                </div>
              ))}
              {event.resource.currentTasks.length > 2 && (
                <div className="text-xs text-gray-500">
                  +{event.resource.currentTasks.length - 2} more
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );

    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div>{eventContent}</div>
          </TooltipTrigger>
          <TooltipContent side="top" align="start" className="w-80">
            <TasksTooltip event={event} tasks={event.resource.currentTasks} />
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  } else if (event.resource?.isTask) {
    return <TaskEvent event={event} onBadgeClick={onBadgeClick} />;
  }

  return (
    <div
      className="flex items-center space-x-2 text-xs cursor-pointer"
      onClick={(e) => {
        e.stopPropagation();
        onBadgeClick(event);
      }}
    >
      <span>{event.title}</span>
    </div>
  );
};

/* ---------------------------
   Cards used in dialog (EventCard, TaskCard, HolidayCard, LeaveCard)
   (kept logic and fields intact)
   --------------------------- */

const OrgEventsCard = ({ event }) => {
  const { title, type, start_time, end_time, notes, dates } =
    event.resource || event;

  // Helper to format date strings
  const formatDate = (dateStr) => format(new Date(dateStr), "EEE, MMM d, yyyy");

  // Detect consecutive dates
  const isConsecutive = (arr) => {
    if (!arr || arr.length < 2) return false;
    const sorted = [...arr].sort(
      (a, b) => new Date(a).getTime() - new Date(b).getTime(),
    );
    for (let i = 1; i < sorted.length; i++) {
      const diff =
        (new Date(sorted[i]) - new Date(sorted[i - 1])) / (1000 * 60 * 60 * 24);
      if (diff > 1) return false;
    }
    return true;
  };

  // Handle date display
  let dateDisplay;
  if (Array.isArray(dates) && dates.length > 0) {
    if (dates.length === 1) {
      dateDisplay = <span>{formatDate(dates[0])}</span>;
    } else if (isConsecutive(dates)) {
      const sorted = [...dates].sort(
        (a, b) => new Date(a).getTime() - new Date(b).getTime(),
      );
      dateDisplay = (
        <span>
          {formatDate(sorted[0])} → {formatDate(sorted[sorted.length - 1])}
        </span>
      );
    } else {
      const sorted = [...dates].sort(
        (a, b) => new Date(a).getTime() - new Date(b).getTime(),
      );
      dateDisplay = (
        <ul className="list-disc list-inside space-y-0.5">
          {sorted.map((d, i) => (
            <li key={i}>{formatDate(d)}</li>
          ))}
        </ul>
      );
    }
  } else {
    dateDisplay = <span>—</span>;
  }

  return (
    <div className="border rounded-xl bg-card/50 shadow-sm p-4 space-y-3">
      {/* Header Row */}
      <div className="flex justify-between items-center">
        <Badge variant="outline" className="capitalize px-2 py-1 text-xs">
          {type || "Event"}
        </Badge>
        {start_time && end_time && (
          <Badge className="px-2 py-1 text-xs bg-blue-100 text-blue-800">
            {start_time} - {end_time}
          </Badge>
        )}
      </div>

      {/* Title */}
      <h4 className="font-semibold text-base text-foreground">
        {title || "Untitled Event"}
      </h4>

      {/* Notes */}
      {notes && (
        <p className="text-sm text-muted-foreground border-l-2 pl-3 border-muted-foreground/20 italic">
          {notes}
        </p>
      )}

      {/* Info Section */}
      <div className="rounded-lg bg-muted/30 p-3 text-sm space-y-2">
        <div>
          <span className="text-muted-foreground block mb-1">
            Date{Array.isArray(dates) && dates.length > 1 ? "s:" : ":"}
          </span>
          <div className="font-medium">{dateDisplay}</div>
        </div>
      </div>
    </div>
  );
};

const EventCard = ({ event }) => (
  <div className="border rounded-lg p-3 bg-card">
    <div className="flex justify-between items-start mb-2">
      <Badge variant="outline" className="mb-2 capitalize">
        {event.resource?.isPresent ? "Attendance" : "Event"}
      </Badge>

      {event.resource?.isPresent &&
        event.resource?.hoursWorked !== undefined && (
          <div
            className={`text-sm font-medium ${event.resource.varianceFlag === "NEGATIVE" ? "text-destructive" : "text-green-600"}`}
          >
            {event.resource.hoursWorked.toFixed(1)}h
          </div>
        )}
    </div>

    <h4 className="font-semibold text-base mb-2">{event.title}</h4>

    {event.resource?.isPresent && (
      <div className="space-y-2">
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div>
            <span className="text-muted-foreground">Check-in: </span>
            {event.resource.checkInTime
              ? format(new Date(event.resource.checkInTime), "h:mm a")
              : "N/A"}
          </div>
          <div>
            <span className="text-muted-foreground">Check-out: </span>
            {event.resource.checkOutTime
              ? format(new Date(event.resource.checkOutTime), "h:mm a")
              : "N/A"}
          </div>
        </div>

        {event.resource.currentTasks &&
          event.resource.currentTasks.length > 0 && (
            <div className="mt-2">
              <div className="text-sm font-medium text-muted-foreground mb-1">
                Tasks:
              </div>
              <div className="space-y-1">
                {event.resource.currentTasks.map((task, taskIndex) => (
                  <div
                    key={taskIndex}
                    className="text-sm pl-2 border-l-2 border-primary"
                  >
                    <div className="font-medium">{task.task_title}</div>
                    {task.project_name && (
                      <div className="text-muted-foreground text-xs">
                        Project: {task.project_name}
                      </div>
                    )}
                    {task.due_date && (
                      <div className="text-muted-foreground text-xs">
                        Due: {format(new Date(task.due_date), "MMM d, yyyy")}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
      </div>
    )}
  </div>
);

const TaskCard = ({ event }) => {
  const isInProgress =
    event.resource?.eventType === "taskbar" ||
    !event.resource?.status ||
    event.resource?.status === "In Progress";
  const isBacklog = event.resource?.status === "Backlog";
  const isDone = event.resource?.status === "Done";

  return (
    <div className="border rounded-lg p-3 bg-card">
      <div className="flex justify-between items-start mb-2">
        <Badge variant="outline" className="mb-2 capitalize">
          {isInProgress
            ? "Task In Progress"
            : isBacklog
              ? "Backlog Task"
              : isDone
                ? "Completed Task"
                : "Task"}
        </Badge>

        {event.resource?.estimate_hours && (
          <div className="text-sm font-medium text-muted-foreground">
            {event.resource.estimate_hours}h
          </div>
        )}
      </div>

      <h4 className="font-semibold text-base mb-2">{event.title}</h4>

      <div className="space-y-1 text-sm">
        {event.resource?.project_name && (
          <div>
            <span className="text-muted-foreground">Project: </span>
            {event.resource.project_name}
          </div>
        )}
        {event.resource?.priority && (
          <div>
            <span className="text-muted-foreground">Priority: </span>
            <Badge variant="outline" className="ml-1 capitalize">
              {event.resource.priority}
            </Badge>
          </div>
        )}
        {event.resource?.due_date && (
          <div>
            <span className="text-muted-foreground">Due: </span>
            {format(new Date(event.resource.due_date), "MMM d, yyyy")}
          </div>
        )}
        {isDone && event.resource?.completed_at && (
          <div>
            <span className="text-muted-foreground">Completed: </span>
            {format(new Date(event.resource.completed_at), "MMM d, yyyy")}
          </div>
        )}
      </div>
    </div>
  );
};
const HolidayCard = ({ event }) => {
  const { name, description, is_paid, _id, dates } = event.resource || {};

  // Helper to format dates nicely
  const formatDate = (dateStr) => format(new Date(dateStr), "EEE, MMM d, yyyy");

  // Detect if the dates are consecutive
  const isConsecutive = (arr) => {
    if (!arr || arr.length < 2) return false;
    const sorted = [...arr].sort(
      (a, b) => new Date(a).getTime() - new Date(b).getTime(),
    );
    for (let i = 1; i < sorted.length; i++) {
      const diff =
        (new Date(sorted[i]) - new Date(sorted[i - 1])) / (1000 * 60 * 60 * 24);
      if (diff > 1) return false; // Not consecutive
    }
    return true;
  };

  let dateDisplay;
  if (Array.isArray(dates) && dates.length > 0) {
    if (dates.length === 1) {
      // Single date
      dateDisplay = <span>{formatDate(dates[0])}</span>;
    } else if (isConsecutive(dates)) {
      // Continuous range
      const sorted = [...dates].sort(
        (a, b) => new Date(a).getTime() - new Date(b).getTime(),
      );
      dateDisplay = (
        <span>
          {formatDate(sorted[0])} → {formatDate(sorted[sorted.length - 1])}
        </span>
      );
    } else {
      // Multiple distinct dates
      const sorted = [...dates].sort(
        (a, b) => new Date(a).getTime() - new Date(b).getTime(),
      );
      dateDisplay = (
        <ul className="list-disc list-inside space-y-0.5">
          {sorted.map((d, i) => (
            <li key={i}>{formatDate(d)}</li>
          ))}
        </ul>
      );
    }
  } else {
    dateDisplay = <span>—</span>;
  }

  return (
    <div className="border rounded-xl bg-card/50 shadow-sm p-4 space-y-3">
      {/* Header Row */}
      <div className="flex justify-between items-center">
        <Badge variant="outline" className="capitalize px-2 py-1 text-xs">
          Holiday
        </Badge>
        {is_paid !== undefined && (
          <Badge
            className={`px-2 py-1 text-xs ${
              is_paid
                ? "bg-green-100 text-green-800"
                : "bg-red-100 text-red-800"
            }`}
          >
            {is_paid ? "Paid" : "Unpaid"}
          </Badge>
        )}
      </div>

      {/* Name */}
      <h4 className="font-semibold text-base text-foreground">
        {name || event.title}
      </h4>

      {/* Description */}
      {description && (
        <p className="text-sm text-muted-foreground border-l-2 pl-3 border-muted-foreground/20 italic">
          {description}
        </p>
      )}

      {/* Info Section */}
      <div className="rounded-lg bg-muted/30 p-3 text-sm space-y-2">
        <div>
          <span className="text-muted-foreground block mb-1">
            Date{Array.isArray(dates) && dates.length > 1 ? "s:" : ":"}
          </span>
          <div className="font-medium">{dateDisplay}</div>
        </div>
      </div>
    </div>
  );
};

const GoogleEventCard = ({ event }) => {
  const {
    summary,
    description,
    location,
    start,
    end,
    allDay,
    meetLink,
    htmlLink,
    attendees,
    organizer,
  } = event.resource || {};

  const safeFormat = (value, pattern) => {
    if (!value) return null;
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : format(d, pattern);
  };

  let timeDisplay = "All day";
  if (!allDay) {
    const startLabel = safeFormat(start, "MMM d, h:mm a");
    const endLabel = safeFormat(end, "h:mm a");
    timeDisplay = startLabel
      ? endLabel
        ? `${startLabel} – ${endLabel}`
        : startLabel
      : "All day";
  }

  const responseClass = (status) =>
    status === "accepted"
      ? "text-green-700"
      : status === "declined"
        ? "text-red-700"
        : status === "tentative"
          ? "text-amber-700"
          : "text-muted-foreground";

  return (
    <div className="border rounded-xl bg-card/50 shadow-sm p-4 space-y-3">
      {/* Header Row */}
      <div className="flex justify-between items-center gap-2">
        <Badge className="px-2 py-1 text-xs bg-red-100 text-red-800 hover:bg-red-100">
          Google Calendar
        </Badge>
        <Badge variant="outline" className="px-2 py-1 text-xs whitespace-nowrap">
          {timeDisplay}
        </Badge>
      </div>

      {/* Title */}
      <h4 className="font-semibold text-base text-foreground">
        {summary || event.title || "(No title)"}
      </h4>

      {/* Location */}
      {location && (
        <div className="flex items-start gap-1.5 text-sm text-muted-foreground">
          <MapPin className="h-4 w-4 mt-0.5 shrink-0" />
          <span>{location}</span>
        </div>
      )}

      {/* Description */}
      {description && (
        <p className="text-sm text-muted-foreground border-l-2 pl-3 border-muted-foreground/20 italic whitespace-pre-wrap">
          {description}
        </p>
      )}

      {/* Attendees */}
      {Array.isArray(attendees) && attendees.length > 0 && (
        <div className="rounded-lg bg-muted/30 p-3 text-sm space-y-1">
          <span className="text-muted-foreground block mb-1">
            Attendees ({attendees.length})
          </span>
          <ul className="space-y-1">
            {attendees.map((a, i) => (
              <li key={i} className="flex justify-between gap-2">
                <span className="truncate">{a.email}</span>
                <span
                  className={`text-xs capitalize shrink-0 ${responseClass(a.responseStatus)}`}
                >
                  {a.responseStatus === "needsAction"
                    ? "No response"
                    : a.responseStatus || "—"}
                </span>
              </li>
            ))}
          </ul>
          {organizer && (
            <div className="pt-1 text-xs text-muted-foreground">
              Organizer: {organizer}
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      {(meetLink || htmlLink) && (
        <div className="flex flex-wrap items-center gap-3 pt-1">
          {meetLink && (
            <a
              href={meetLink}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center justify-center gap-2 rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white shadow transition-colors hover:bg-green-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <Video className="h-4 w-4" />
              Join Google Meet
            </a>
          )}
          {htmlLink && (
            <a
              href={htmlLink}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1 text-sm text-blue-600 underline-offset-4 hover:underline"
            >
              Open in Google Calendar
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          )}
        </div>
      )}
    </div>
  );
};

const LeaveCard = ({ event }) => (
  <div className="border rounded-lg p-3 bg-card">
    <div className="flex justify-between items-start mb-2">
      <Badge variant="outline" className="mb-2 capitalize">
        Leave
      </Badge>
    </div>

    <h4 className="font-semibold text-base mb-2">{event.title}</h4>

    <div className="text-sm">
      <div>
        <span className="text-muted-foreground">Type: </span>
        {event.resource.type} Leave
      </div>
      {event.resource.reason && (
        <div className="mt-1">
          <span className="text-muted-foreground">Reason: </span>
          {event.resource.reason}
        </div>
      )}
    </div>
  </div>
);

/* ---------------------------
   Main BigCalendar component
   --------------------------- */
const BigCalendar = ({
  events = [],
  month,
  leaveDetails = [],
  userTasksSummary,
}) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTasks, setSelectedTasks] = useState([]);
  console.log("User task summary:", userTasksSummary);
  useEffect(() => {
    if (month) setCurrentDate(new Date(month));
  }, [month]);

  // helper: filter events for exact date
  const eventsForDate = (date) => {
    return events.filter((ev) => {
      const s = ev.start ? new Date(ev.start) : null;
      const e = ev.end ? new Date(ev.end) : s;
      if (!s) return false;
      return dateInRange(date, s, e);
    });
  };

  const handleSelectSlot = (slotInfo) => {
    const { start } = slotInfo;
    setSelectedDate(start);
    setSelectedTasks(eventsForDate(start));
  };

  const handleBadgeClick = (event) => {
    const eventDate = new Date(event.start);

    //To show total work force hours
    if (event.resource?.isAggregatedAttendance) {
      // show all attendance for this day
      setSelectedDate(eventDate);
      setSelectedTasks(event.resource.attendanceList);
      return;
    }

    //Default behaviour
    setSelectedDate(eventDate);
    setSelectedTasks(eventsForDate(eventDate));
  };

  // DAY styling: add top green ribbon if date falls inside any holiday event span.
  const dayPropGetter = (date) => {
    // find any holiday events that cover this date
    const holidaysOnDate = events.filter(
      (ev) => ev.resource?.isHoliday && dateInRange(date, ev.start, ev.end),
    );
    if (holidaysOnDate.length > 0) {
      // single green ribbon at top of the cell (connected across days)
      return {
        style: {
          // ribbon: full width, 6px height at top
          backgroundImage:
            "linear-gradient(to bottom, rgba(34,197,94,1) 0%, rgba(34,197,94,1) 6px, transparent 6px)",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "top left",
          backgroundSize: "100% 6px",
        },
      };
    }

    // events-based highlights for normal events (optional subtle)
    const eventsOnDate = events.filter((ev) =>
      dateInRange(date, ev.start, ev.end),
    );
    if (eventsOnDate.some((ev) => ev.resource?.isEvent)) {
      return {
        style: {
          backgroundColor: "#eff6ff", // subtle blue tint for normal events
        },
      };
    }

    return {};
  };

  // EVENT styling: neutralize holiday box background so ribbon remains the clear visual indicator.
  const eventPropGetter = (event) => {
    // Keep tasks/present/events coloring similar to before, but make holiday event background transparent.
    if (event.resource?.isHoliday) {
      return {
        style: {
          backgroundColor: "transparent",
          border: "none",
          padding: 0,
          color: "#065f46", // make holiday title green
        },
      };
    }

    if (event.resource?.isEvent) {
      return {
        style: {
          backgroundColor: "transparent",
          border: "none",
        },
      };
    }

    if (event.resource?.isGoogleEvent) {
      return {
        style: {
          backgroundColor: "transparent",
          border: "none",
          padding: 0,
        },
      };
    }

    // The chip carries its own colour, so strip the default event box around it.
    if (event.resource?.isLeaveSummary) {
      return {
        style: {
          backgroundColor: "transparent",
          border: "none",
          padding: 0,
        },
      };
    }

    if (event.resource?.isPresent) {
      return {
        style: {
          backgroundColor: "transparent",
          border: "none",
          padding: 0,
        },
      };
    }

    if (event.resource?.isTask) {
      const isTaskbar = event.resource.eventType === "taskbar";
      const isBacklog = event.resource.status === "Backlog";
      const isDone = event.resource.status === "Done";

      let backgroundColor = "#e0f2fe";
      if (isTaskbar) backgroundColor = "#ede9fe";
      else if (isBacklog) backgroundColor = "#fef2f2";
      else if (isDone) backgroundColor = "#f0fdf4";

      return {
        style: {
          backgroundColor,
          border: "none",
          borderRadius: "4px",
          padding: "2px 4px",
        },
      };
    }

    return {};
  };

  // wrapper to attach onBadgeClick
  const CustomEventWrapper = (props) => (
    <CustomEvent {...props} onBadgeClick={handleBadgeClick} />
  );

  /* ================================
      TO SHOW TOTAL WORKFORCE HOURS
  ================================ */

  const aggregateAttendanceEvents = (events) => {
    const grouped = {};

    events.forEach((ev) => {
      if (ev.resource?.isPresent) {
        const day = format(new Date(ev.start), "yyyy-MM-dd");
        if (!grouped[day]) {
          grouped[day] = {
            start: ev.start,
            end: ev.end,
            title: "Workforce Hours",
            resource: {
              isAggregatedAttendance: true,
              totalHours: 0,
              attendanceList: [],
            },
          };
        }

        grouped[day].resource.totalHours += ev.resource.hoursWorked || 0;
        grouped[day].resource.attendanceList.push(ev);
      }
    });

    // return non-attendance events + aggregated attendance events
    const nonAttendance = events.filter((ev) => !ev.resource?.isPresent);
    return [...nonAttendance, ...Object.values(grouped)];
  };

  return (
    <>
      <div className="h-[70vh] bg-white p-4 rounded-lg border">
        <Calendar
          localizer={localizer}
          dayMaxEvents={3}
          popup
          // events={events}
          events={aggregateAttendanceEvents(events)} //To show total workforce hours
          startAccessor="start"
          endAccessor="end"
          date={currentDate}
          onNavigate={(date) => setCurrentDate(date)}
          style={{ height: "100%" }}
          views={["month", "week"]}
          components={{
            event: CustomEventWrapper,
            month: {
              // preserve your leave tooltip on date header
              dateHeader: ({ date }) => {
                const leaveEntry = leaveDetails.find(
                  (l) =>
                    format(new Date(l.date), "yyyy-MM-dd") ===
                    format(date, "yyyy-MM-dd"),
                );

                return (
                  <div
                    className="rbc-date-cell cursor-pointer relative"
                    onClick={() => handleSelectSlot({ start: date })}
                  >
                    <span>{date.getDate()}</span>

                    {leaveEntry && leaveEntry.count > 0 && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="absolute right-1 top-1">
                              <Badge variant="secondary">
                                {leaveEntry.count}
                              </Badge>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent side="top">
                            <div className="text-sm">
                              <div className="font-semibold mb-1">
                                On Leave:
                              </div>
                              <ul className="list-disc pl-4">
                                {leaveEntry.users_on_leave.map((user, idx) => (
                                  <li key={idx}>{user}</li>
                                ))}
                              </ul>
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </div>
                );
              },
            },
          }}
          dayPropGetter={dayPropGetter}
          eventPropGetter={eventPropGetter}
          onShowMore={(moreEvents, date) => {
            setSelectedDate(date);
            setSelectedTasks(moreEvents);
          }}
          selectable
          onSelectSlot={handleSelectSlot}
        />
      </div>

      <Dialog open={!!selectedDate} onOpenChange={() => setSelectedDate(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader className="pb-2">
            <DialogTitle className="text-lg">
              {selectedDate && format(selectedDate, "EEEE, MMMM d, yyyy")}
            </DialogTitle>
          </DialogHeader>

          <div className="overflow-y-auto flex-1 py-2">
            <div className="space-y-3">
              {selectedTasks.length === 0 ? (
                <div className="text-center py-4 text-muted-foreground">
                  No events scheduled for this day
                </div>
              ) : (
                <>
                  {(() => {
                    const selectedDateStr = selectedDate
                      ? format(selectedDate, "yyyy-MM-dd")
                      : null;
                    const leaveEntry = leaveDetails.find(
                      (l) => l.date === selectedDateStr,
                    );

                    return (
                      <>
                        {leaveEntry && leaveEntry.users_on_leave.length > 0 && (
                          <div className="mb-4">
                            <h3 className="font-semibold text-md mb-3 text-muted-foreground border-b pb-1 flex items-center justify-between">
                              <span>On Leave</span>
                              <Badge variant="secondary" className="ml-2">
                                {leaveEntry.count}
                              </Badge>
                            </h3>
                            <ul className="list-disc pl-6 space-y-1 text-sm">
                              {leaveEntry.users_on_leave.map((user, idx) => (
                                <li key={idx}>
                                  <span className="font-medium">
                                    {user.name}
                                  </span>{" "}
                                  <span className="text-muted-foreground">
                                    (
                                    {format(new Date(user.start_date), "MMM d")}{" "}
                                    – {format(new Date(user.end_date), "MMM d")}
                                    )
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {(() => {
                          const presentEvents = selectedTasks.filter(
                            (event) => event.resource?.isPresent,
                          );
                          const holidayEvents = selectedTasks.filter(
                            (event) => event.resource?.isHoliday,
                          );
                          const leaveEvents = selectedTasks.filter(
                            (event) => event.resource?.isLeave,
                          );
                          const regularEvents = selectedTasks.filter(
                            (event) => event.resource?.isEvent,
                          );
                          const googleCalendarEvents = selectedTasks.filter(
                            (event) => event.resource?.isGoogleEvent,
                          );

                          const taskEvents = selectedTasks.filter(
                            (event) => event.resource?.isTask,
                          );
                          const inProgressTasks = taskEvents.filter(
                            (task) =>
                              task.resource?.eventType === "taskbar" ||
                              !task.resource?.status ||
                              task.resource?.status === "In Progress",
                          );
                          const backlogDoneTasks = taskEvents.filter(
                            (task) =>
                              task.resource?.status === "Backlog" ||
                              task.resource?.status === "Done",
                          );

                          return (
                            <>
                              {presentEvents.length > 0 && (
                                <div className="mb-4">
                                  <h3 className="font-semibold text-md mb-3 text-muted-foreground border-b pb-1 flex items-center justify-between">
                                    <span>Attendance</span>
                                    <Badge variant="secondary" className="ml-2">
                                      {presentEvents.length}
                                    </Badge>
                                  </h3>
                                  <div className="space-y-3">
                                    {presentEvents.map((event, index) => (
                                      <EventCard
                                        key={`present-${index}`}
                                        event={event}
                                      />
                                    ))}
                                  </div>
                                </div>
                              )}

                              {inProgressTasks.length > 0 && (
                                <div className="mb-4">
                                  <h3 className="font-semibold text-md mb-3 text-muted-foreground border-b pb-1 flex items-center justify-between">
                                    <span>Tasks In Progress</span>
                                    <Badge variant="secondary" className="ml-2">
                                      {inProgressTasks.length}
                                    </Badge>
                                  </h3>
                                  <div className="space-y-3">
                                    {inProgressTasks.map((event, index) => (
                                      <TaskCard
                                        key={`inprogress-${index}`}
                                        event={event}
                                      />
                                    ))}
                                  </div>
                                </div>
                              )}

                              {backlogDoneTasks.length > 0 && (
                                <div className="mb-4">
                                  <h3 className="font-semibold text-md mb-3 text-muted-foreground border-b pb-1 flex items-center justify-between">
                                    <span>Task Status</span>
                                    <Badge variant="secondary" className="ml-2">
                                      {backlogDoneTasks.length}
                                    </Badge>
                                  </h3>
                                  <div className="space-y-3">
                                    {backlogDoneTasks.map((event, index) => (
                                      <TaskCard
                                        key={`status-${index}`}
                                        event={event}
                                      />
                                    ))}
                                  </div>
                                </div>
                              )}

                              {holidayEvents.length > 0 && (
                                <div className="mb-4">
                                  <h3 className="font-semibold text-md mb-3 text-muted-foreground border-b pb-1 flex items-center justify-between">
                                    <span>Holidays</span>
                                    <Badge variant="secondary" className="ml-2">
                                      {holidayEvents.length}
                                    </Badge>
                                  </h3>
                                  <div className="space-y-3">
                                    {holidayEvents.map((event, index) => (
                                      <HolidayCard
                                        key={`holiday-${index}`}
                                        event={event}
                                      />
                                    ))}
                                  </div>
                                </div>
                              )}

                              {leaveEvents.length > 0 && (
                                <div className="mb-4">
                                  <h3 className="font-semibold text-md mb-3 text-muted-foreground border-b pb-1 flex items-center justify-between">
                                    <span>Leaves</span>
                                    <Badge variant="secondary" className="ml-2">
                                      {leaveEvents.length}
                                    </Badge>
                                  </h3>
                                  <div className="space-y-3">
                                    {leaveEvents.map((event, index) => (
                                      <LeaveCard
                                        key={`leave-${index}`}
                                        event={event}
                                      />
                                    ))}
                                  </div>
                                </div>
                              )}

                              {regularEvents.length > 0 && (
                                <div className="mb-4">
                                  <h3 className="font-semibold text-md mb-3 text-muted-foreground border-b pb-1 flex items-center justify-between">
                                    <span>Events</span>
                                    <Badge variant="secondary" className="ml-2">
                                      {regularEvents.length}
                                    </Badge>
                                  </h3>
                                  <div className="space-y-3">
                                    {regularEvents.map((event, index) => (
                                      <OrgEventsCard
                                        key={`event-${index}`}
                                        event={event}
                                      />
                                    ))}
                                  </div>
                                </div>
                              )}

                              {googleCalendarEvents.length > 0 && (
                                <div className="mb-4">
                                  <h3 className="font-semibold text-md mb-3 text-muted-foreground border-b pb-1 flex items-center justify-between">
                                    <span>Google Calendar</span>
                                    <Badge variant="secondary" className="ml-2">
                                      {googleCalendarEvents.length}
                                    </Badge>
                                  </h3>
                                  <div className="space-y-3">
                                    {googleCalendarEvents.map(
                                      (event, index) => (
                                        <GoogleEventCard
                                          key={`google-${event.resource?.id || index}`}
                                          event={event}
                                        />
                                      ),
                                    )}
                                  </div>
                                </div>
                              )}

                              {userTasksSummary &&
                                userTasksSummary.length > 0 &&
                                selectedDate &&
                                (() => {
                                  const selectedDateStr = format(
                                    selectedDate,
                                    "yyyy-MM-dd",
                                  );
                                  const todayStr = format(
                                    new Date(),
                                    "yyyy-MM-dd",
                                  );

                                  const allTasksForDate =
                                    userTasksSummary.flatMap((user) => {
                                      const completed =
                                        user.completed?.filter(
                                          (task) =>
                                            task.date === selectedDateStr,
                                        ) || [];
                                      const ongoing =
                                        selectedDateStr === todayStr
                                          ? user.ongoing || []
                                          : [];
                                      return [...completed, ...ongoing];
                                    });

                                  if (allTasksForDate.length === 0) return null;

                                  return (
                                    <div className="mt-6 border-t pt-4">
                                      <h3 className="text-lg font-semibold mb-3">
                                        Task Summary —{" "}
                                        {format(
                                          selectedDate,
                                          "EEE, MMM d, yyyy",
                                        )}
                                      </h3>

                                      {userTasksSummary.map((user) => {
                                        const filteredCompleted =
                                          user.completed?.filter(
                                            (task) =>
                                              task.date === selectedDateStr,
                                          ) || [];

                                        const filteredOngoing =
                                          selectedDateStr === todayStr
                                            ? user.ongoing || []
                                            : [];

                                        if (
                                          filteredCompleted.length === 0 &&
                                          filteredOngoing.length === 0
                                        )
                                          return null;

                                        return (
                                          <div
                                            key={user.user_id}
                                            className="border rounded-lg p-4 bg-muted/30 mb-4 shadow-sm"
                                          >
                                            <h4 className="text-base font-medium mb-2">
                                              {user.user_name}
                                            </h4>

                                            {filteredOngoing.length > 0 && (
                                              <div className="mb-2">
                                                <h5 className="text-sm font-semibold text-blue-600 mb-1">
                                                  Ongoing Tasks
                                                </h5>
                                                <ul className="list-disc list-inside space-y-1 text-sm">
                                                  {filteredOngoing.map(
                                                    (task, i) => (
                                                      <li
                                                        key={i}
                                                        className="flex justify-between"
                                                      >
                                                        <span>
                                                          {task.title}
                                                        </span>
                                                        <Badge
                                                          variant="outline"
                                                          className="capitalize"
                                                        >
                                                          {task.status}
                                                        </Badge>
                                                      </li>
                                                    ),
                                                  )}
                                                </ul>
                                              </div>
                                            )}

                                            {filteredCompleted.length > 0 && (
                                              <div>
                                                <h5 className="text-sm font-semibold text-green-600 mb-1">
                                                  Completed Tasks
                                                </h5>
                                                <ul className="list-disc list-inside space-y-1 text-sm">
                                                  {filteredCompleted.map(
                                                    (task, i) => (
                                                      <li
                                                        key={i}
                                                        className="flex justify-between"
                                                      >
                                                        <span>
                                                          {task.title}
                                                        </span>
                                                        <Badge
                                                          variant="outline"
                                                          className="capitalize"
                                                        >
                                                          {task.status}
                                                        </Badge>
                                                      </li>
                                                    ),
                                                  )}
                                                </ul>
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  );
                                })()}
                            </>
                          );
                        })()}
                      </>
                    );
                  })()}
                </>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BigCalendar;
